import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useDotLegalQueryArray } from "@common/hooks/dotLegalReactQuery";
import { EntityType } from "@common/Common.types";
import { useTranslation } from "@localization/useTranslation";
import {
    entityGraphNodeKey,
    EntityGraphApiModel,
    EntityGraphEdge,
    EntityGraphEdgeProvenance,
    EntityGraphNode,
    parseEntityGraphNodeKey,
} from "./EntityGraph.types";

const entityGraphUrl = (rootType: number, rootId: string, riskAreaId: string | undefined) =>
    `/entity-graph?rootType=${rootType}&rootId=${rootId}` + (riskAreaId ? `&riskAreaId=${riskAreaId}` : "");

// A process uses processing activities, assets and legal entities, so it sits above all of them; every edge
// then flows from a lower typeOrder to a higher-or-equal one, which the layered layout relies on.
const typeOrders: Record<number, number> = {
    [EntityType.Process]: 0,
    [EntityType.ProcessingActivity]: 1,
    [EntityType.System]: 2,
    [EntityType.LegalEntity]: 3,
};

export interface UseEntityGraphDialogProps {
    rootType: EntityType;
    rootId: string;
    riskAreaId?: string;
}

export function useEntityGraphDialog({ rootType, rootId, riskAreaId }: UseEntityGraphDialogProps) {
    const [root, setRoot] = useState({ type: rootType as number, id: rootId });
    const { translateString } = useTranslation();

    const { data, isLoading } = useDotLegalQueryArray<EntityGraphApiModel>(
        ["entityGraph", root.type, root.id, riskAreaId ?? null],
        entityGraphUrl(root.type, root.id, riskAreaId),
        { placeholderData: keepPreviousData }
    );

    const typeLabels: Record<number, string> = {
        [EntityType.ProcessingActivity]: translateString("processingActivity"),
        [EntityType.System]: translateString("system"),
        [EntityType.LegalEntity]: translateString("legalEntity"),
        [EntityType.Process]: translateString("process"),
    };

    const nodes: Array<EntityGraphNode> = (data?.nodes ?? []).map((node) => ({
        key: entityGraphNodeKey(node.type, node.id),
        label: node.name,
        typeId: node.type,
        typeLabel: typeLabels[node.type] ?? "",
        typeOrder: typeOrders[node.type] ?? 99,
        leafChips: node.linkChips,
    }));

    // Multiple relationship kinds between the same pair (e.g. a legal entity that is both data processor and
    // disclosure recipient of one processing activity) collapse into a single drawn edge carrying all kinds.
    const edgesByPair = new Map<string, EntityGraphEdge>();
    (data?.edges ?? []).forEach((edge) => {
        const fromKey = entityGraphNodeKey(edge.fromType, edge.fromId);
        const toKey = entityGraphNodeKey(edge.toType, edge.toId);
        const pairKey = `${fromKey}->${toKey}`;
        const existing = edgesByPair.get(pairKey);
        if (existing) {
            existing.kinds.push(edge.kind);
            // Dashed only if EVERY coexisting kind is off the confirmed chain — one confirmed kind keeps the
            // drawn edge solid. (Can't happen with today's edge kinds since SubProcessor is the only LE→LE kind
            // and never shares a pair with a non-sub kind, but AND is the correct merge if that ever changes.)
            existing.dashed = existing.dashed && edge.provenance === EntityGraphEdgeProvenance.Declared;
        } else {
            edgesByPair.set(pairKey, { fromKey, toKey, kinds: [edge.kind], dashed: edge.provenance === EntityGraphEdgeProvenance.Declared });
        }
    });

    const onRootChange = (key: string) => {
        const { type, id } = parseEntityGraphNodeKey(key);
        setRoot({ type, id });
    };

    return {
        nodes,
        edges: [...edgesByPair.values()],
        rootKey: entityGraphNodeKey(root.type, root.id),
        onRootChange,
        risk: data?.risk ?? undefined,
        isLoading,
        hasData: !!data,
    };
}
